<template>
  <div class="profile-personal">
    <export-modal :visible.sync="visibleExportModal" />
    <actualize-modal
      :visible.sync="visibleActualizeModal"
      @updateProfile="$emit('edit')"
      @updateRate="$emit('updateRate')"
    />

    <a-modal
      title="Manage Assignments"
      :visible="visibleAssignmentsModal"
      :destroy-on-close="true"
      width="900px"
      @cancel="visibleAssignmentsModal = false"
    >
      <profile-assignments v-if="visibleAssignmentsModal" @open="$emit('openAssignment', $event)" />
      <template slot="footer">
        <div class="flex justify-content-between">
          <a-button @click="visibleAssignmentsModal = false">Close</a-button>
        </div>
      </template>
    </a-modal>

    <a-modal
      title="Manage Permissions"
      :visible="visiblePermissionsModal"
      width="900px"
      @cancel="visiblePermissionsModal = false"
    >
      <profile-permissions />
      <template slot="footer">
        <div class="flex justify-content-between">
          <a-button @click="visiblePermissionsModal = false">Close</a-button>
        </div>
      </template>
    </a-modal>

    <a-modal title="Manage Notes" :visible="visibleNotesModal" width="700px" @cancel="visibleNotesModal = false">
      <profile-notes />
      <template slot="footer">
        <div class="flex justify-content-between">
          <a-button @click="visibleNotesModal = false">Close</a-button>
        </div>
      </template>
    </a-modal>

    <div class="profile-personal-summary">
      <user-avatar :width="72" :height="72" :image-path="associate.photo_url ? associate.photo_url : undefined" />
      <div class="profile-personal-name">{{ associate.first_name }} {{ associate.last_name }}</div>
    </div>

    <div class="profile-personal-grid">
      <section class="profile-personal-section">
        <section-divider title="Contacts" />
        <div class="profile-info-grid">
          <div class="profile-info-field">
            <span>Email</span>
            <strong class="flex gap-2">
              {{ associate.email || ' - ' }}
              <span v-if="associate.email" class="action-button" @click="handleCopy(associate.email)">
                <a-icon icon="carbon:copy" width="18" height="18" />
              </span>
            </strong>
          </div>
          <div class="profile-info-field">
            <span>Phone</span>
            <strong class="flex gap-2"
              >{{ associate.phone || ' - ' }}
              <span v-if="associate.phone" class="action-button" @click="handleCopy(associate.phone)">
                <a-icon icon="carbon:copy" width="18" height="18" /> </span
            ></strong>
          </div>
        </div>
      </section>

      <section class="profile-personal-section">
        <section-divider title="Identifiers" />
        <div class="profile-info-grid">
          <div class="profile-info-field">
            <span>Associate Number</span>
            <strong class="flex gap-2">
              {{ associate.associate_number }}
              <span class="action-button" @click="handleCopy(associate.associate_number)">
                <a-icon icon="carbon:copy" width="18" height="18" />
              </span>
            </strong>
          </div>
          <div class="profile-info-field">
            <span>{{ getTaxIdentifierLabel(associate.is_ssn) }}</span>
            <strong>{{ associate.social_security_number ?? ' - ' }}</strong>
          </div>
          <div v-if="!isStaff" class="profile-info-field">
            <span>External Staff</span>
            <strong>{{ associate.staff_associate ? 'Yes' : 'No' }}</strong>
          </div>
        </div>
      </section>

      <section class="profile-personal-section">
        <section-divider title="Personal Data" />
        <div class="profile-info-grid">
          <div class="profile-info-field">
            <span>Birth Date</span>
            <strong>{{ associate.birth_date ? formatDateString(associate.birth_date) : ' - ' }}</strong>
          </div>
          <div class="profile-info-field">
            <span>Address</span>
            <strong>{{ fullAddress }}</strong>
          </div>
        </div>
      </section>

      <section class="profile-personal-section profile-personal-actions">
        <section-divider title="Actions" />
        <div class="profile-personal-actions-list">
          <a-button
            type="primary"
            v-if="
              hasUncontrolledPermission('manage_associates', {
                id: associate.id
              }) &&
              associate &&
              associate.project_id === $store.state.applicationState.currentProject.id
            "
            @click="$emit('edit')"
            icon="form"
            data-testid="profile-view-edit-button"
            >{{ associate.is_verified ? 'Edit' : 'Edit & Complete' }}</a-button
          >
          <a-button icon="export" @click="visibleExportModal = true">Export Profile</a-button>
          <a-button icon="form" @click="visibleAssignmentsModal = true">Manage Assignments</a-button>
          <a-button
            v-if="
              (associate.payment_info?.legal_entity &&
                associate.payment_info?.legal_entity.staffing_status === 'linked') ||
              (!associate.payment_info?.legal_entity && associate.staff_associate)
            "
            icon="link"
            @click="linkStuff(associate.staff_associate ? 'unlink_from_staff_associate' : 'link_to_staff_associate')"
          >
            {{ associate.staff_associate ? 'Disconnect External Staff' : 'Connect External Staff' }}
          </a-button>
          <a-button icon="usergroup-add" @click="$emit('clone')">Create New Profile</a-button>
          <a-button icon="mail" @click="openSmsSendModal">Send Notification</a-button>
          <a-button
            v-if="sequenceActionConfig"
            :loading="loadingSendToSequence"
            icon="mail"
            @click="openSendToSequenceConfirm"
          >
            {{ sequenceActionConfig.buttonText }}
          </a-button>
          <a-button :loading="loadingSync" icon="reload" @click="synchronizeShift">Synchronize Shift</a-button>
          <a-button v-if="associate.payment_info?.legal_entity" icon="eye" @click="$emit('open')">
            View Legal Entity
          </a-button>
          <a-button icon="reload" @click="openActualizeShift">Update unpaid shift rates</a-button>
          <a-button icon="file-protect" @click="visiblePermissionsModal = true">Manage Permissions</a-button>
          <a-button icon="form" @click="visibleNotesModal = true">Manage Notes</a-button>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { Button, message, Modal, notification } from 'ant-design-vue'
import { createNamespacedHelpers } from 'vuex'
import constData from '@/helpers/const-data'
import moment from 'moment-timezone'
import UserAvatar from '@/components/ui/user-avatar.vue'
import ProfileActualizeShift from './profile-actualize-shift.vue'
import ProfileViewExport from './profile-view-export.vue'
import util from '@/util'
import { getTaxIdentifierLabel } from '@/utils/tax-identifier'
import { getAssociateProfileCompletionErrorMessage } from '@/modules/profile/utils/associate-profile-completion'
import ProfileViewPermissions from './profile-view-permissions.vue'
import ProfileViewNotes from './profile-view-notes.vue'
import ProfileViewAssignments from './profile-view-assignments.vue'
import SectionDivider from '@/components/ui/section-divider.vue'
import permissionMixin from '@/mixins/permissionMixin.js'

const { mapActions: profileActions, mapState: profileState } = createNamespacedHelpers('profile')
const { mapActions: modalActions } = createNamespacedHelpers('modals')

export default {
  components: {
    'a-button': Button,
    'a-modal': Modal,
    'user-avatar': UserAvatar,
    'actualize-modal': ProfileActualizeShift,
    'export-modal': ProfileViewExport,
    'profile-permissions': ProfileViewPermissions,
    'profile-notes': ProfileViewNotes,
    'profile-assignments': ProfileViewAssignments,
    'section-divider': SectionDivider
  },
  emits: ['clone', 'edit', 'openAssignment'],
  data() {
    return {
      loadingSync: false,
      loadingTransfer: false,
      loadingExport: false,
      loadingLink: false,
      loadingSendToSequence: false,
      pendingSequenceSend: false,

      countriesList: constData.countries,
      statesList: constData.usStates,

      visibleActualizeModal: false,
      visibleExportModal: false,
      visiblePermissionsModal: false,
      visibleNotesModal: false,
      visibleAssignmentsModal: false
    }
  },
  mixins: [permissionMixin],
  computed: {
    ...profileState({
      associate: state => state.associate
    }),

    isStaff() {
      return this.$store.state.applicationState.currentProject.staffing
    },

    fullAddress() {
      const address = this.associate.address ?? {}
      const parts = [
        address.address_line1,
        address.address_line2,
        address.city,
        this.findStateByCode(address.state),
        this.findCountryByCode(address.country),
        address.zipcode
      ].filter(Boolean)

      return parts.length ? parts.join(', ') : ' - '
    },

    normalizedPaymentType() {
      const paymentType = this.associate?.payment_info?.type
      return typeof paymentType === 'string' ? paymentType.toLowerCase() : ''
    },

    sequenceActionConfig() {
      if (this.normalizedPaymentType === 'employee') {
        return {
          label: 'Onboarding',
          buttonText: 'Send to Onboarding',
          confirmTitle: 'Send to Onboarding?',
          confirmContent: 'Are you sure you want to send this associate to onboarding?',
          successMessage: 'Associate has been successfully sent to onboarding'
        }
      }

      if (this.normalizedPaymentType === 'contractor') {
        return {
          label: 'Orientation',
          buttonText: 'Send to Orientation',
          confirmTitle: 'Send to Orientation?',
          confirmContent: 'Are you sure you want to send this associate to orientation?',
          successMessage: 'Associate has been successfully sent to orientation'
        }
      }

      return null
    }
  },
  methods: {
    ...modalActions(['openModal']),
    ...profileActions([
      'sendExportedAssociate',
      'downloadExportedAssociate',
      'synchronizeAssociateShifts',
      'postTransferAssociateRecords',
      'postLinkToStuffAssociate',
      'postCreateStuffAssociate',
      'postAssociateSendToOnboarding',
      'reloadAssociate'
    ]),

    formatDateString(dateString) {
      return moment(dateString).utc().format('MM/DD/YYYY')
    },

    getTaxIdentifierLabel,

    findStateByCode(code) {
      return this.statesList.find(item => item.code.toLowerCase() === code?.toLowerCase())?.name
    },

    findCountryByCode(code) {
      return this.countriesList.find(item => item.code.toLowerCase() === code?.toLowerCase())?.name
    },

    showNotification(type, message, description) {
      notification[type]({
        message,
        description
      })
    },

    handleCopy(text) {
      navigator.clipboard
        .writeText(text)
        .then(() => {
          message.success('Successfully copied!', 5)
        })
        .catch(() => {
          message.error('Unable copy email!', 5)
        })
    },

    openSmsSendModal() {
      this.openModal({
        type: 'notification',
        data: {
          visible: true,
          associate_number: this.associate.associate_number
        }
      })
    },

    openSendToSequenceConfirm() {
      if (!this.sequenceActionConfig) return

      const profileCompletionError = getAssociateProfileCompletionErrorMessage(this.associate)

      if (profileCompletionError) {
        this.pendingSequenceSend = true
        this.showNotification('warning', 'Validation Error', profileCompletionError)
        this.$emit('edit')

        return
      }

      Modal.confirm({
        title: this.sequenceActionConfig.confirmTitle,
        content: this.sequenceActionConfig.confirmContent,
        okText: 'Send',
        onOk: async () => {
          this.loadingSendToSequence = true

          try {
            const payload = {
              associate_id: this.associate.id
            }

            await this.postAssociateSendToOnboarding(payload)

            this.showNotification('success', 'Success', this.sequenceActionConfig.successMessage)
            this.reloadAssociate(true)
          } catch (error) {
            this.showNotification('error', 'Error', error.message || error)
          } finally {
            this.loadingSendToSequence = false
          }
        },
        onCancel() {}
      })
    },

    continuePendingSequenceAction() {
      if (!this.pendingSequenceSend) {
        return
      }

      this.pendingSequenceSend = false
      this.openSendToSequenceConfirm()
    },

    clearPendingSequenceAction() {
      this.pendingSequenceSend = false
    },

    openActualizeShift() {
      Modal.confirm({
        title: 'Review unpaid shift rates',
        content: 'Choose a rate and period, then review the changes before applying them to unpaid shifts.',
        okText: 'Review rates',
        onOk: () => {
          this.visibleActualizeModal = true
        },
        onCancel() {}
      })
    },

    async sendReport(email) {
      this.loadingExport = true

      const payload = {
        associate_id: this.associate.id,
        email: email.toLowerCase()
      }

      try {
        await this.sendExportedAssociate(payload)

        this.showNotification('success', 'Success', 'Exported Associate Profile successfully sent to email')
      } catch (error) {
        this.showNotification('error', 'Error', error.message || error)
      } finally {
        this.loadingExport = false
      }
    },

    async downloadReport() {
      this.loadingExport = true
      try {
        const url = await this.downloadExportedAssociate({
          associate_id: this.associate.id
        })

        util.downloadFile(url)
      } catch (error) {
        this.showNotification('error', 'Error', error.message || error)
      } finally {
        this.loadingExport = false
      }
    },

    async synchronizeShift() {
      this.loadingSync = true

      try {
        await this.synchronizeAssociateShifts({
          associate_id: this.associate.id
        })

        this.showNotification('success', 'Success', 'Shifts have been successfully synchronize')
      } catch (error) {
        this.showNotification('error', 'Error', error.message || error)
      } finally {
        this.loadingSync = false
      }
    },

    async transferRecords() {
      this.loadingTransfer = true

      try {
        await this.postTransferAssociateRecords({
          associate_id: this.associate.id
        })

        this.showNotification('success', 'Success', 'Records have been successfully transferring')
      } catch (error) {
        this.showNotification('error', 'Error', error.message || error)
      } finally {
        this.loadingTransfer = false
      }
    },

    async linkStuff(type) {
      Modal.confirm({
        title: type === 'link_to_staff_associate' ? 'Connect Profiles?' : 'Disconnect Profiles?',
        content:
          type === 'link_to_staff_associate'
            ? 'By connecting, the associate’s shifts will be SYNCHRONIZED. The Staff Company will be able to pay them through their own payroll. However, some profile changes will be partially unavailable. Are you sure you want to connect the profiles?'
            : 'By disconnecting, shift SYNCHRONIZATION will STOP. The associate will no longer be able to receive payment for shifts through the Staff Company. However, all restrictions on profile changes will be removed. Are you sure you want to disconnect the profiles?',
        onOk: async () => {
          this.loadingLink = true

          try {
            const data = await this.postLinkToStuffAssociate({
              id: this.associate.id,
              type
            })

            if (type === 'link_to_staff_associate' && !data.can_link) {
              await this.createStuff()
            }

            this.reloadAssociate(true)
          } catch (error) {
            this.showNotification('error', 'Error', error.message || error)
          } finally {
            this.loadingLink = false
          }
        },
        onCancel() {}
      })
    },

    async createStuff() {
      if (this.$store.state.applicationState.currentProject.id !== 1) {
        this.showNotification(
          'info',
          'Info',
          'We couldn’t Connect the Profiles! The person doesn’t work in the Staff Company and doesn’t have a profile there. Please contact the Staff Company manager to resolve the issue!'
        )
        return
      }

      await new Promise((resolve, reject) => {
        Modal.confirm({
          title: 'Create a New Profile?',
          content:
            'We couldn’t find a profile in the Staff Company to establish the connection. Please clarify the employment details for this person. You can copy the profile from your company into the Staffing Company by creating a new profile. Would you like to do that?',
          okText: 'Create New Profile',
          onOk: async () => {
            try {
              await this.postCreateStuffAssociate({
                id: this.associate.id
              })

              resolve()
            } catch (error) {
              this.showNotification('error', 'Error', 'Unable to Create a New Profile')
              resolve()
            }
          },
          onCancel: () => {
            resolve()
          }
        })
      })
    }
  }
}
</script>

<style scoped>
.profile-personal {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.profile-personal-summary {
  display: flex;
  align-items: center;
  gap: 16px;
}

.profile-personal-name {
  color: var(--heading-color);
  font-size: 22px;
  font-weight: 700;
}

.profile-personal-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px 24px;
}

.profile-personal-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.profile-info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
}

.profile-info-field {
  min-width: 0;
}

.profile-info-field > span {
  display: block;
  margin-bottom: 4px;
  color: var(--text-color-secondary);
  font-size: 12px;
}

.profile-info-field > strong {
  display: block;
  overflow-wrap: anywhere;
}

.profile-personal-actions-list {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

@media screen and (max-width: 1024px) {
  .profile-personal-grid,
  .profile-personal-actions-list {
    grid-template-columns: 1fr;
  }
}
</style>
